import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IAggregateFilterEvent, IFilterBarTab } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { FilterBarService } from "@app/directives/_resourceFilters/filter-bar/filter-bar.service";
import { IVehicle } from "@app/interfaces/IVehicle";
import { FoundationNavigationModule } from "@app/main/modules/foundation/foundation.navigation";
import { CalcType, FtCloudConfigExceptionError } from "@app/main/modules/foundation/_enums/camera-configs/CameraConfigEnums";
import { IAccountGroupConfigDto, ICameraDeviceParameterRecord } from "@app/main/modules/foundation/_models/_camera-configs/CameraConfigModels";
import { AccountService } from "@app/main/modules/foundation/_services/account.service";
import { ConfigAccountGroupService } from "@app/main/modules/foundation/_services/config-account-group.service";
import { IAccount } from "@app/services/account.service";
import { IStreamaxCameraProfile, StreamaxCameraProfileService } from "@app/services/camera/streamax-camera-profile-service";
import { NavigationService } from "@app/services/navigation.service";
import { PreferencesService } from "@app/services/preferences.service";
import { TranslationService } from "@app/services/translation.service";
import { ITimeZoneInfo } from "@app/userPreferences/model/ITimeZoneInfo";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, EMPTY, from } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import './streamax-configs-account-list.component.scss';
import { StreamaxConfigsPercentageBarService } from "./streamax-configs-percentage-bar.service";

@UntilDestroy()
@Component({
    selector: 'streamax-configs-account-list',
    template: require('./streamax-configs-account-list.component.html')
})
export class StreamaxConfigsAccountListComponent implements OnInit {

    accountGroupConfigs: IAccountGroupConfigDto[];

    selectedAccountConfig: IAccountGroupConfigDto;
    selectedAccountName: string;
    selectedAccountProfile: IStreamaxCameraProfile;
    filteredRecords: IAccountGroupConfigDto[];

    accounts: Map<number, IAccount>;
    accountsList: IAccount[];
    accountsWithProfile: number[];

    profilesList: IStreamaxCameraProfile[];

    vehiclesList: IVehicle[];

    tableColumns: IVTableColumn<IAccountGroupConfigDto>[];
    tableRows: IVTableRow[];

    loading: boolean = false;

    showAccountDrilldownView: boolean = false
    showProfileEditor: boolean = false;

    timezones: ITimeZoneInfo[];

    profileIsSaveable: boolean = false;

    filterTabs: IFilterBarTab<IAccountGroupConfigDto>[];
    filterEvent: IAggregateFilterEvent<IAccountGroupConfigDto>;

    get getErrorMax(): number {
        return Object.keys(FtCloudConfigExceptionError).filter(key => isNaN(Number(key))).length;
    }

    constructor(
        private readonly filterBarService: FilterBarService,
        private readonly accountService: AccountService,
        private readonly streamaxCameraProfileService: StreamaxCameraProfileService,
        private readonly vTableService: VTableService,
        private readonly cd: ChangeDetectorRef,
        private readonly navigationService: NavigationService,
        private readonly configAccountGroupService: ConfigAccountGroupService,
        private readonly preferencesService: PreferencesService,
        private readonly percentageBarService: StreamaxConfigsPercentageBarService,
        private readonly translationService: TranslationService) 
    { }

    ngOnInit(): void {
        this.createTabs();
        this.loadData();
    }

    rowClicked($event: any): void {
        this.navigationService.updateBreadcrumbNameResolver({
            configAccountBreadcrumbKey: this.accountsList.find(a => a.Id == $event.data.AccountId).CompanyName,
        });

        this.navigationService.go(FoundationNavigationModule.StreamaxConfigsAccountView, { accountId: $event.data.AccountId })
    }

    backToAccountView(): void {
        this.selectedAccountConfig = null;
        this.showAccountDrilldownView = false;
        this.selectedAccountName = null;
        this.cd.detectChanges();
    }

    toggleNewAccountProfileEditor(): void {
        this.showProfileEditor = !this.showProfileEditor;
    }

    filterCameraByAccount(record: ICameraDeviceParameterRecord, account: IAccount): boolean {
        return record.vehicle?.AccountId === account.Id;
    }

    accountSearchPredicate(account: IAccount, searchText: string): boolean {
        return account.CompanyName.toLowerCase().indexOf(searchText.toLowerCase()) >= 0;
    }

    profileIsInSavableState($event: boolean): void {
        this.profileIsSaveable = $event

        this.cd.detectChanges();
    }

    addNewAccountProfile(newProfile: IStreamaxCameraProfile): void {
        const newAccountGroup = {
            Id: newProfile.AccountId,
            AccountId: newProfile.AccountId,
            PeripheralWithConfigRecord: [],
            CombinedErrors: 0
        }
        
        this.accountGroupConfigs = [
            ...this.accountGroupConfigs, newAccountGroup
        ]
        
        this.streamaxCameraProfileService.updateProfileToCache(newProfile);

        this.showProfileEditor = !this.showProfileEditor;
    }

    onFilterChanged($event: any): void {

        if (!$event.trigger) {
            return;
        }

        this.filterEvent = $event;

        this.filteredRecords = this.filterEvent.filteredData;
    }

    private loadData(): void {
        this.createTable();
        this.loading = true;

        combineLatest([
            this.configAccountGroupService.getConfigAccountGroups(),
            this.accountService.getCustomerAccounts(),
            this.streamaxCameraProfileService.getProfiles(),
            from(this.preferencesService.getTimezones() as Promise<ITimeZoneInfo[]>)
        ])
            .pipe(
                untilDestroyed(this),
                tap(([accountGroups, accounts, profiles, timezones]) => {
                    this.accounts = new Map(accounts.map(account => [account.Id, account]));
                    this.accountsList = accounts;
                    this.accountGroupConfigs = accountGroups;
                    
                    this.profilesList = profiles.filter(p => this.accounts.has(p.AccountId));
                    this.accountsWithProfile = this.profilesList.map(p => p.AccountId);

                    this.timezones = timezones.filter(t => this.streamaxTimezoneValues.includes(t.Id));

                    const nonAssociationProfiles = this.profilesList.filter(p => !this.accountGroupConfigs.some(a => a.AccountId === p.AccountId));

                    nonAssociationProfiles.forEach(p => {
                        this.accountGroupConfigs.push({
                            Id: p.AccountId,
                            AccountId: p.AccountId,
                            PeripheralWithConfigRecord: [],
                            CombinedErrors: 0
                        })
                    });                    
                    
                    this.tableRows = this.vTableService.createTableRows(this.accountGroupConfigs, this.tableColumns);

                    this.loading = false;
                }),
                catchError(err => {
                    console.error('Error loading data:', err);
                    this.loading = false;
                    return EMPTY;
                })
            ).subscribe();
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Account", width: 250, minTableWidth: 450 }),
                createCellFunction: (accountGroup) => this.vTableService.createTableCell(this.accounts.get(accountGroup.AccountId).CompanyName)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "StreamaxConfigs.ActiveSet", width: 120 }),
                createCellFunction: (accountGroup) => this.vTableService.createTableCell(
                    this.profilesList.find(p => p.AccountId === accountGroup.AccountId)?.IsActive ?
                        this.translationService.t("StreamaxConfigs.AssessAndSet") :
                        this.translationService.t("StreamaxConfigs.AssessOnly"))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "StreamaxConfigs.DevicesCount", width: 120 }),
                createCellFunction: (accountGroup) => this.vTableService.createTableCell(accountGroup.PeripheralWithConfigRecord.length)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "StreamaxConfigs.ConfigsCollected", excludeFromExport: true }),
                createCellFunction: (accountGroup) => this.vTableService.createPercentageBarCell(
                    this.percentageBarService.getPercentageBarModel(accountGroup, CalcType.DeviceComms)
                )
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "StreamaxConfigs.TotalAlignedMetrics", excludeFromExport: true }),
                createCellFunction: (accountGroup) => this.vTableService.createPercentageBarCell(
                    this.percentageBarService.getPercentageBarModel(accountGroup, CalcType.TotalErrors)
                )
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "StreamaxConfigs.TotalAlignedDevices", excludeFromExport: true }),
                createCellFunction: (accountGroup) => this.vTableService.createPercentageBarCell(
                    this.percentageBarService.getPercentageBarModel(accountGroup, CalcType.TotalSync)
                )
            }
        ]
    }

    private createTabs(): void {
        this.filterTabs = [];

        this.filterTabs.push(this.filterBarService.createTabDefinition<IAccountGroupConfigDto>("Common.All", () => true));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IAccountGroupConfigDto>("StreamaxConfigs.AllUnmatched", this.IsUnmatchedPredicate));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IAccountGroupConfigDto>("StreamaxConfigs.PartialMatch", this.isPartiallyMatchedPredicate));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IAccountGroupConfigDto>("StreamaxConfigs.Matching", this.isMatchedPredicate));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IAccountGroupConfigDto>("StreamaxConfigs.NoAssociations", this.isNoAssociationPredicate));
    }

    private IsUnmatchedPredicate = (dto: IAccountGroupConfigDto) => {
        if (!dto.PeripheralWithConfigRecord.length) {
            return false;
        }

        return !dto.PeripheralWithConfigRecord.some(p =>
            p.LatestRecord?.CameraDeviceParameterSettings?.ProfileExceptionErrors?.Errors !== undefined &&
            p.LatestRecord.CameraDeviceParameterSettings.ProfileExceptionErrors.Errors.length === 0
        );
    }

    private isPartiallyMatchedPredicate = (dto: IAccountGroupConfigDto) => {
        const records = dto.PeripheralWithConfigRecord;
        if (!records || records.length === 0) return false;

        // True if at least one record has the Errors array defined and empty.
        const hasEmpty = records.some(p =>
            p.LatestRecord?.CameraDeviceParameterSettings?.ProfileExceptionErrors?.Errors !== undefined &&
            p.LatestRecord.CameraDeviceParameterSettings.ProfileExceptionErrors.Errors.length === 0
        );

        // True if at least one record is NOT in that state (either Errors is undefined or non-empty)
        const hasNonEmpty = records.some(p =>
            !(
                p.LatestRecord?.CameraDeviceParameterSettings?.ProfileExceptionErrors?.Errors !== undefined &&
                p.LatestRecord.CameraDeviceParameterSettings.ProfileExceptionErrors.Errors.length === 0
            )
        );

        return hasEmpty && hasNonEmpty;
    }

    private isMatchedPredicate = (dto: IAccountGroupConfigDto) => {
        if (!dto.PeripheralWithConfigRecord.length) {
            return false;
        }

        return dto.PeripheralWithConfigRecord.length > 0 &&
            dto.PeripheralWithConfigRecord.every(p =>
                p.LatestRecord?.CameraDeviceParameterSettings?.ProfileExceptionErrors?.Errors !== undefined &&
                p.LatestRecord.CameraDeviceParameterSettings.ProfileExceptionErrors.Errors.length === 0
            );
    }

    private isNoAssociationPredicate = (record: IAccountGroupConfigDto) => {
        return !record.PeripheralWithConfigRecord.length;
    }

    streamaxTimezoneValues: string[] = [
        "Dateline Standard Time",
        "UTC-11",
        "UTC-11",
        "Hawaiian Standard Time",
        "Alaskan Standard Time",
        "Pacific Standard Time",
        "Pacific Standard Time (Mexico)",
        "Mountain Standard Time (Mexico)",
        "Mountain Standard Time",
        "US Mountain Standard Time",
        "Central Standard Time (Mexico)",
        "Canada Central Standard Time",
        "Central Standard Time",
        "Central America Standard Time",
        "SA Pacific Standard Time",
        "Eastern Standard Time",
        "US Eastern Standard Time",
        "Venezuela Standard Time",
        "Atlantic Standard Time",
        "Central Brazilian Standard Time",
        "SA Western Standard Time",
        "Pacific SA Standard Time",
        "Paraguay Standard Time",
        "Newfoundland Standard Time",
        "E. South America Standard Time",
        "Argentina Standard Time",
        "Greenland Standard Time",
        "SA Eastern Standard Time",
        "Montevideo Standard Time",
        "UTC-02",
        "Mid-Atlantic Standard Time",
        "Cape Verde Standard Time",
        "Azores Standard Time",
        "GMT Standard Time",
        "Morocco Standard Time",
        "Greenwich Standard Time",
        "UTC",
        "W. Europe Standard Time",
        "Central Europe Standard Time",
        "Romance Standard Time",
        "Central European Standard Time",
        "W. Central Africa Standard Time",
        "Namibia Standard Time",
        "GTB Standard Time",
        "Middle East Standard Time",
        "Syria Standard Time",
        "South Africa Standard Time",
        "FLE Standard Time",
        "Egypt Standard Time",
        "E. Europe Standard Time",
        "GTB Standard Time",
        "Israel Standard Time",
        "Arabic Standard Time",
        "Arab Standard Time",
        "Russian Standard Time",
        "E. Africa Standard Time",
        "Iran Standard Time",
        "Arabian Standard Time",
        "Caucasus Standard Time",
        "Azerbaijan Standard Time",
        "Georgian Standard Time",
        "Mauritius Standard Time",
        "Afghanistan Standard Time",
        "West Asia Standard Time",
        "Ekaterinburg Standard Time",
        "Pakistan Standard Time",
        "India Standard Time",
        "Sri Lanka Standard Time",
        "Nepal Standard Time",
        "Central Asia Standard Time",
        "N. Central Asia Standard Time",
        "Myanmar Standard Time",
        "North Asia Standard Time",
        "SE Asia Standard Time",
        "China Standard Time",
        "Singapore Standard Time",
        "W. Australia Standard Time",
        "Taipei Standard Time",
        "Ulaanbaatar Standard Time",
        "North Asia East Standard Time",
        "Tokyo Standard Time",
        "Korea Standard Time",
        "Yakutsk Standard Time",
        "Cen. Australia Standard Time",
        "AUS Central Standard Time",
        "E. Australia Standard Time",
        "Vladivostok Standard Time",
        "West Pacific Standard Time",
        "Tasmania Standard Time",
        "AUS Eastern Standard Time",
        "Central Pacific Standard Time",
        "New Zealand Standard Time",
        "Fiji Standard Time",
        "UTC+12",
        "Tonga Standard Time"
    ];
}
